/**
 * Decide whether a `returnUrl` query param on the login page is safe to redirect to after a
 * successful login.
 *
 * The API sends anonymous users hitting the OAuth authorize endpoint to
 * `{dashboard}/login?returnUrl=<url-encoded api /oauth/authorize URL>` so they can resume the
 * OAuth flow once authenticated. This is the open-redirect guard for that hop: only an
 * absolute https URL on a `*.qhslab.com` host with an `/oauth/...` path is honored. Anything
 * else - a different host (including a lookalike like `qhslab.com.evil.com`), a non-https
 * scheme, a protocol-relative `//host/...` URL, a bare relative path, or a same-host path
 * outside `/oauth/` - is rejected so the caller can fall back to the normal post-login
 * destination instead of an off-site (or same-site-but-wrong-path) redirect.
 *
 * @param {string | null | undefined} raw  The raw `returnUrl` query-string value.
 * @returns {boolean}
 */
export const isSafeOAuthReturnUrl = (raw) => {
  if (!raw) return false;
  try {
    const u = new URL(raw);
    const hostOk = u.protocol === 'https:' && /(^|\.)qhslab\.com$/i.test(u.hostname);
    const pathOk = u.pathname.startsWith('/oauth/');
    return hostOk && pathOk;
  } catch {
    return false;
  }
};

/**
 * Decide whether `UserProvider.jsx`'s default post-login "navigate to the dashboard" effect
 * should be skipped for the current page location.
 *
 * `Login.jsx` performs a full cross-origin `window.location.assign(returnUrl)` back to the
 * API's OAuth authorize endpoint on the same successful-login transition that also flips
 * `metadata.currentAccount.uuid`, which independently triggers `UserProvider`'s in-app
 * `navigateToRoute` to the default dashboard route. When a valid OAuth `returnUrl` is present,
 * that in-app navigation is redundant (the browser is about to unload the page) and produces a
 * visible route-change flash before the OAuth redirect fires, so it should be skipped.
 *
 * Takes the raw `location.search` string (rather than reading `window.location` itself) so the
 * decision is a pure function that can be unit tested without mocking the DOM.
 *
 * @param {string} search  The current `location.search`, e.g. `?returnUrl=...`.
 * @returns {boolean}
 */
export const shouldSkipDefaultNavigationForOAuthReturn = (search) => {
  const returnUrl = new URLSearchParams(search).get('returnUrl');
  return isSafeOAuthReturnUrl(returnUrl);
};

/**
 * Path of the dashboard-hosted OAuth consent screen. Kept in sync with the `/oauth/consent`
 * route and the API's `DashboardConsentPath` - the API SSO-redirects the browser here with an
 * opaque `?request_id=`.
 */
export const OAUTH_CONSENT_PATH = '/oauth/consent';

/**
 * Whether the given pathname is the standalone OAuth consent screen (tolerating a trailing slash).
 *
 * @param {string} pathname  The current `location.pathname`.
 * @returns {boolean}
 */
export const isOAuthConsentPath = (pathname) =>
  pathname === OAUTH_CONSENT_PATH || pathname === `${OAUTH_CONSENT_PATH}/`;

/**
 * Whether `UserProvider`'s default post-auth "navigate to the dashboard / initPath" effect should
 * be skipped for the current location. True in two cases, both of which own their own URL and must
 * not be routed away by the in-app default navigation:
 *
 *   1. The login `?returnUrl=` hop - `SignInForm` is about to `window.location.assign` back to the
 *      API's authorize endpoint (see `shouldSkipDefaultNavigationForOAuthReturn`).
 *   2. The consent screen - the API sends the browser to `/oauth/consent?request_id=<opaque>` and
 *      the page reads `request_id` straight from the query. `initPath` is parsed from the pathname
 *      ONLY, so the default in-app navigation re-routes to the bare `/oauth/consent`, drops the
 *      `?request_id`, and the page then shows "No connection request was found".
 *
 * @param {string} pathname  The current `location.pathname`.
 * @param {string} search    The current `location.search`.
 * @returns {boolean}
 */
export const shouldSkipDefaultNavigationForOAuth = (pathname, search) =>
  isOAuthConsentPath(pathname) || shouldSkipDefaultNavigationForOAuthReturn(search);
